import React, { useMemo, useEffect, useRef } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import db from '@/db';
import { useCurrentPerson } from '../hooks/useCurrentPerson';
import type { Program, Site } from '@/db/types/user';

interface ProgramSiteSelectorProps {
  clientId: number;
  templateId?: number;
  selectedProgramId: number | null;
  selectedSiteId: number | null;
  onProgramChange: (id: number | null) => void;
  onSiteChange: (id: number | null) => void;
  programRequired?: boolean;
  autoSelect?: boolean;
}

export default function ProgramSiteSelector({
  clientId,
  templateId,
  selectedProgramId,
  selectedSiteId,
  onProgramChange,
  onSiteChange,
  programRequired = false,
  autoSelect = false,
}: ProgramSiteSelectorProps) {
  const { organizationId } = useCurrentPerson();

  const client = useLiveQuery(() => db.clients.get(clientId), [clientId]);

  const rawAllPrograms = useLiveQuery(async () => {
    if (!organizationId) return [];
    return db.programs.where('organization_id').equals(organizationId).toArray();
  }, [organizationId]);

  const rawAllSites = useLiveQuery(async () => {
    if (!organizationId) return [];
    return db.sites.where('organization_id').equals(organizationId).toArray();
  }, [organizationId]);

  const allPrograms = rawAllPrograms ?? [];
  const allSites = rawAllSites ?? [];

  // Check if the template has program_disabled
  const template = useLiveQuery(async () => {
    if (!templateId) return null;
    return db.documentTemplates.get(templateId) ?? null;
  }, [templateId]);

  const isProgramDisabled = template?.program_disabled === true;

  // Programs available for this document: all org programs (already user-scoped by the API),
  // filtered only by the template's acceptable_programs (when restricted). This mirrors the
  // online contract (create-permitted ∩ acceptable_programs);
  const availablePrograms = useMemo<Program[]>(() => {
    if (isProgramDisabled) return [];
    let programs = allPrograms;
    if (template && template.all_programs === false) {
      const acceptableIds = new Set((template.acceptable_programs ?? []).map(p => p.id));
      programs = programs.filter(p => acceptableIds.has(p.id));
    }
    return programs;
  }, [allPrograms, isProgramDisabled, template]);

  // Sites available for this document: all org sites (already user-scoped by the API),
  // filtered by the client's site_ids if present
  const availableSites = useMemo<Site[]>(() => {
    if (!client?.site_ids) return allSites;
    return allSites.filter(s => client.site_ids!.includes(s.id));
  }, [client?.site_ids, allSites]);

  // Auto-select when only one option exists (only once, as a default)
  const didAutoSelect = useRef({ program: false, site: false });

  useEffect(() => {
    if (autoSelect && !didAutoSelect.current.program && availablePrograms.length === 1 && selectedProgramId === null) {
      didAutoSelect.current.program = true;
      onProgramChange(availablePrograms[0].id);
    }
  }, [autoSelect, availablePrograms, selectedProgramId, onProgramChange]);

  useEffect(() => {
    if (autoSelect && !didAutoSelect.current.site && availableSites.length === 1 && selectedSiteId === null) {
      didAutoSelect.current.site = true;
      onSiteChange(availableSites[0].id);
    }
  }, [autoSelect, availableSites, selectedSiteId, onSiteChange]);

  const showProgramSelector = !isProgramDisabled;
  const showSiteSelector = availableSites.length > 0;

  return (
    <div className="space-y-6">
      {showProgramSelector && (
        <div>
          <label htmlFor="program-select" className="block text-sm font-medium text-gray-700 mb-1">
            Program {programRequired && <span className="text-red-500">*</span>}
          </label>
          <select
            id="program-select"
            value={selectedProgramId ?? ''}
            onChange={(e) => onProgramChange(e.target.value ? Number(e.target.value) : null)}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-sm"
          >
            <option value="">Select a program...</option>
            {availablePrograms.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </div>
      )}

      {showSiteSelector && (
        <div>
          <label htmlFor="site-select" className="block text-sm font-medium text-gray-700 mb-1">
            Site
          </label>
          <select
            id="site-select"
            value={selectedSiteId ?? ''}
            onChange={(e) => onSiteChange(e.target.value ? Number(e.target.value) : null)}
            className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 sm:text-sm"
          >
            <option value="">All sites</option>
            {availableSites.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      )}

      {!showProgramSelector && !showSiteSelector && (
        <p className="text-sm text-gray-500">No program or site selection needed for this template.</p>
      )}
    </div>
  );
}
